/**
 * AI Clinical Note Generation via Medplum Bot (`ai-clinical-note-generation`).
 */
import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Appointment, Identifier, QuestionnaireResponse } from '@medplum/fhirtypes';
import { AI_GENERATED_PROVENANCE_EXT } from '@unify/fhir-model/constants';
import {
  chimeTranscriptStatus,
  getChimeSession,
  getSessionTranscriptPointer,
  isSessionTranscriptAvailable,
  type ChimeTranscriptStatus,
} from '@unify/fhir-model/resources/appointment';
import { executeChimeTelehealth } from './telehealth/chime/chimeTelehealthService';
import {
  createAiGenerationTraceId,
  createTimingRecorder,
} from './timing/aiNotetakerTiming';
import { UserFacingError } from './userFacingError';

export const AI_NOTE_GENERATION_BOT_IDENTIFIER = {
  system: 'https://unify.health/bots',
  value: 'ai-clinical-note-generation',
} as const;

export type AiNoteGenerationExecuteInput = {
  appointmentId: string;
  providerPrompt?: string;
};

const AI_NOTE_DISABLED_DEFAULT_MESSAGE = 'AI documentation assistance is disabled for this provider or client.';

/** Authored disabled messages from medplum-server/src/bots/ai-clinical-note-generation.ts; only these exact strings are shown. */
export const AI_NOTE_DISABLED_MESSAGES: ReadonlySet<string> = new Set([
  AI_NOTE_DISABLED_DEFAULT_MESSAGE,
  'Session transcription is not consented for this client, so AI cannot use the meeting transcript.',
]);

type AiNoteGenerationDisabledResult = {
  disabled: true;
  /** Untrusted server value; only a string that is in `AI_NOTE_DISABLED_MESSAGES` is shown. */
  message?: unknown;
};

function isAiNoteGenerationDisabledResult(value: unknown): value is AiNoteGenerationDisabledResult {
  return Boolean(value && typeof value === 'object' && (value as AiNoteGenerationDisabledResult).disabled === true);
}

/**
 * Optional caller-supplied timing context so the click, request, and render events
 * all share one traceId rather than splitting across two internally-generated ones.
 */
export type AiNoteGenerationTimingContext = {
  traceId: string;
  aiRunId?: string;
  scenario?: string;
};

export function getAiNoteGenerationBotExecuteTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_AI_NOTE_GENERATION_BOT_ID?.trim();
  if (id) return id;
  const system =
    import.meta.env.VITE_MEDPLUM_AI_NOTE_GENERATION_BOT_IDENTIFIER_SYSTEM?.trim() ??
    AI_NOTE_GENERATION_BOT_IDENTIFIER.system;
  const value =
    import.meta.env.VITE_MEDPLUM_AI_NOTE_GENERATION_BOT_IDENTIFIER_VALUE?.trim() ??
    AI_NOTE_GENERATION_BOT_IDENTIFIER.value;
  return { system, value };
}

/**
 * True when a stored session transcript is ready for AI: until 7 days after its last write.
 */
export function isSessionTranscriptReady(appointment: Appointment | undefined): boolean {
  const pointer = appointment ? getSessionTranscriptPointer(appointment) : undefined;
  return pointer ? isSessionTranscriptAvailable(pointer) : false;
}

/** Cap on how long an in-progress pipeline status is trusted (and polled) after the session. */
const TRANSCRIPT_PIPELINE_MAX_MS = 3 * 60 * 60 * 1000;

function pipelineHasStalled(appointment: Appointment | undefined, nowMs: number): boolean {
  const refMs = Date.parse(appointment?.end ?? appointment?.start ?? '');
  return Number.isFinite(refMs) && nowMs - refMs > TRANSCRIPT_PIPELINE_MAX_MS;
}

/** A Chime appointment: it has a Chime session or a transcript pointer. Other appointments show no transcript badge. */
export function isChimeTranscriptAppointment(appointment: Appointment | undefined): boolean {
  return Boolean(
    appointment && (getChimeSession(appointment) || getSessionTranscriptPointer(appointment)),
  );
}

export type ChimeTranscriptUiState = { state: ChimeTranscriptStatus | 'idle'; noConsent?: boolean };

/**
 * The Chime appointment badge (design Decision 25), hidden (`idle`) until the session has ended.
 * An ended session with no status was not recorded (recording stopped throughout, or nobody joined).
 */
export function resolveChimeTranscriptUiState(input: {
  appointment: Appointment;
  sessionEnded: boolean;
  nowMs?: number;
}): ChimeTranscriptUiState {
  if (!input.sessionEnded) return { state: 'idle' };
  const derived = chimeTranscriptStatus(input.appointment, input.nowMs);
  return derived ? { state: derived.status, ...(derived.noConsent ? { noConsent: true } : {}) } : { state: 'not-recorded' };
}

/**
 * A draft exists and the transcript was written after it was generated: the page shows
 * "A new part of the transcript is available", and Generate redrafts from the full transcript.
 */
export function hasNewTranscriptPart(
  appointment: Appointment | undefined,
  note: QuestionnaireResponse | undefined,
): boolean {
  const generatedAt = note?.extension
    ?.find((e) => e.url === AI_GENERATED_PROVENANCE_EXT)
    ?.extension?.find((e) => e.url === 'generated-at')?.valueDateTime;
  const generatedMs = generatedAt ? Date.parse(generatedAt) : Number.NaN;
  const pointer = appointment ? getSessionTranscriptPointer(appointment) : undefined;
  if (!pointer || Number.isNaN(generatedMs)) return false;
  return isSessionTranscriptAvailable(pointer) && Date.parse(pointer.writtenAt) > generatedMs;
}

/**
 * Whether the appointment page should keep asking for the transcript status of a Chime appointment:
 * until the status settles, but not forever (a pipeline stuck long after the session stops).
 */
export function shouldPollTranscriptStatus(input: {
  appointment: Appointment | undefined;
  sessionEnded: boolean;
  nowMs?: number;
}): boolean {
  if (!input.appointment || !isChimeTranscriptAppointment(input.appointment)) return false;
  const { state } = resolveChimeTranscriptUiState({ ...input, appointment: input.appointment });
  return (
    (state === 'processing' || state === 'drafting') &&
    !pipelineHasStalled(input.appointment, input.nowMs ?? Date.now())
  );
}

/**
 * Chime pipeline status check via the staff-executable `chime-telehealth` bot, which answers
 * participants with status fields only (the Decision 25 set). The badge derives its state from the
 * Appointment, which is re-read directly (staff can read it).
 */
export async function requestChimeTranscriptStatusCheck(
  medplum: MedplumClient,
  appointmentId: string,
): Promise<{ appointment: Appointment }> {
  await executeChimeTelehealth(medplum, { action: 'check-transcript-status', appointmentId });
  return { appointment: await medplum.readResource('Appointment', appointmentId, { cache: 'no-cache' }) };
}

/**
 * Manually trigger AI note generation for a telehealth appointment.
 *
 * Pass `timingContext` (from the caller's existing TimingRecorder) to ensure all
 * timing events — click, request-sent, response-returned, and UI-render — land on
 * the same trace instead of splitting across two internally-generated trace IDs.
 */
export async function requestAiNoteGeneration(
  medplum: MedplumClient,
  appointmentId: string,
  providerPrompt?: string,
  timingContext?: AiNoteGenerationTimingContext,
): Promise<{ traceId: string; aiRunId?: string }> {
  // Use caller-supplied context when provided so all events share one trace.
  const aiRunId = timingContext?.aiRunId ?? `run-${Date.now().toString(36)}`;
  const traceId = timingContext?.traceId ?? createAiGenerationTraceId(appointmentId, undefined, aiRunId);

  // Only create an internal recorder when the caller hasn't supplied one (standalone use).
  const recorder = timingContext
    ? null
    : createTimingRecorder({ traceId, phase: 'ai_generation', appointmentId, aiRunId });

  recorder?.started('ai_generate_request_prepared_at');

  const payload: AiNoteGenerationExecuteInput & { _traceId?: string; _aiRunId?: string; _scenario?: string } = {
    appointmentId,
    ...(providerPrompt?.trim() ? { providerPrompt: providerPrompt.trim() } : {}),
    _traceId: traceId,
    _aiRunId: aiRunId,
    ...(timingContext?.scenario ? { _scenario: timingContext.scenario } : {}),
  };

  const sendStart = recorder?.now() ?? 0;
  recorder?.started('ai_generate_request_sent_at');

  try {
    const result = await medplum.executeBot(
      getAiNoteGenerationBotExecuteTarget(),
      payload,
      ContentType.JSON,
    );
    if (isAiNoteGenerationDisabledResult(result)) {
      const { message } = result;
      throw new UserFacingError(
        typeof message === 'string' && AI_NOTE_DISABLED_MESSAGES.has(message) ? message : AI_NOTE_DISABLED_DEFAULT_MESSAGE,
      );
    }
    recorder?.finished('ai_generate_response_returned_at', sendStart);
  } catch (err) {
    recorder?.failed('ai_generate_request_failed', err);
    throw err;
  }

  return { traceId, aiRunId };
}
